import { useContext } from "react";
import CurrencyContext from "../contexts/CurrencyContext";
import CartContext from "../contexts/CartContext";
import { HTMLSectionWithLink } from "./elements/Link";
import classnames from "classnames";
import { offerOverThreshold, remainingOfferAmount } from "../core/products";
import AnimationContext from "../contexts/AnimationContext";
import { formatMoney } from "../hooks/useFormatMoney";
import ProductsContext from "../contexts/ProductsContext";
import { Product } from "../domain";
import { getAvailableOfferTargetVariantsSet } from "../core/products/variantUtils";

export const CardElement = ({
  children,
  added,
  isMultipleProduct,
  offerId,
}) => {
  const animationContext = useContext(AnimationContext);

  return (
    <div
      className={classnames(
        `border rounded-2xl p-5 text-gray-900 border-gray-200 Offer-Id-${offerId}`,
        {
          "border-primary-500": added && !isMultipleProduct,
          "Offer-Card-Multiple": isMultipleProduct,
          "Offer-Card-Single": !isMultipleProduct,
          "GiftBox-Card-Eligible": added,
          "opacity-50": animationContext.isAnimationInState(
            "product_choice_removed",
            "after"
          ),
          "opacity-100": animationContext.isAnimationInState(
            "product_choice_removed",
            null
          ),
        }
      )}
      data-cy="card-element"
    >
      {children}
    </div>
  );
};

export function useThreshold(offer) {
  const { cart, collectionsInCart } = useContext(CartContext);
  const { currency, convertPrice } = useContext(CurrencyContext);

  const productsCtx = useContext(ProductsContext);
  const targetProduct = offer?.target_shopify_product_handle
    ? productsCtx.getTargetProduct(offer.target_shopify_product_handle)
    : undefined;

  if (!offer || !cart) {
    return {};
  }

  const amount = remainingOfferAmount(
    offer,
    cart,
    collectionsInCart ? collectionsInCart : []
  );
  const remainingSpend = formatMoney(convertPrice(amount), currency);
  const cartEmpty = cart.item_count === 0;

  let passed = offerOverThreshold(
    offer,
    cart,
    collectionsInCart ? collectionsInCart : []
  );
  let titleText = getTitleText(
    offer,
    passed,
    cartEmpty,
    remainingSpend,
    targetProduct
  );
  return { passed, titleText, amount };
}

function getShopUrl() {
  // @ts-ignore
  // TECH DEBT: window.DATA possibly doesn't exist and is only used for tests
  const shop = window.Shopify?.shop || window.DATA?.SHOP || "unknown_url";
  return shop;
}

function getTitleText(offer, passed, cartEmpty, remainingSpend, targetProduct) {
  if (passed && offer.products && !offer.shopify_product_variant_id) {
    return (
      offer.title_condition_met_product_selection || "Select your free gift"
    );
  }

  if (offer.threshold_mode && offer.threshold_mode === "product_added") {
    return getTitleTextProduct(offer, targetProduct);
  }
  if (offer.threshold_mode && offer.threshold_mode === "collection_added") {
    return getTitleTextCollection(offer);
  }
  if (offer.threshold_mode && offer.threshold_mode === "spend_in_collection") {
    return getTitleTextSpendInCollection(offer, cartEmpty, remainingSpend);
  }
  return getTitleTextFreeFrom(offer, cartEmpty, remainingSpend);
}

function getTitleTextFreeFrom(offer, cartEmpty, remainingSpend) {
  if (cartEmpty) {
    return (offer.title_empty_cart || "Spend REMAININGSPEND").replace(
      "REMAININGSPEND",
      remainingSpend
    );
  }

  return (
    offer.title_condition_not_met || "Spend only REMAININGSPEND more"
  ).replace("REMAININGSPEND", remainingSpend);
}

function getTitleTextProduct(offer, targetProduct?: Product) {
  const text =
    offer.title_condition_not_met ||
    (offer.target_min_quantity > 1
      ? "Buy REQPRODUCTQ× product"
      : "Buy product");
  const title = text.replace("REQPRODUCTQ", String(offer.target_min_quantity));
  const availableTargetVariantsSet = getAvailableOfferTargetVariantsSet(
    offer,
    targetProduct
  );

  const highlightedTargetVariant =
    availableTargetVariantsSet.size === 1 &&
    targetProduct?.variants?.length &&
    // make sure we are not displaying the default variant title, as well
    // as its not the only one existing
    targetProduct.variants.length > 1
      ? targetProduct?.variants?.find(
          (variant) =>
            variant.id === availableTargetVariantsSet.values().next().value
        )
      : null;
  const productLink = `<a href="https://${getShopUrl()}/products/${
    offer.target_shopify_product_handle
  }${
    highlightedTargetVariant ? "?variant=" + highlightedTargetVariant.id : ""
  }">
  ${offer.target_shopify_product_name}${
    highlightedTargetVariant ? ` - ${highlightedTargetVariant.title}` : ""
  }</a>`;

  // Using @DEPRECATED placeholders
  if (title.includes("REQPRODUCTLINK")) {
    return `<div class="break-words font-normal text-sm">${title.replace(
      "REQPRODUCTLINK",
      productLink
    )}</div>`;
  }

  return `<div class="break-words">${title}</div>
          <div class="Giftbox-TextOverflowEllipsis Cropped-Text font-normal text-sm">${productLink}</div>`;
}

function getTitleTextCollection(offer) {
  const text =
    offer.title_condition_not_met ||
    (offer.target_min_quantity > 1
      ? "Buy REQPRODUCTQ× products from the collection"
      : "Buy any product from the collection");
  const title = text.replace("REQPRODUCTQ", String(offer.target_min_quantity));

  const collectionLink = `<a href="https://${getShopUrl()}/collections/${
    offer.target_shopify_collection_handle
  }">${offer.target_shopify_collection_name}</a>`;

  // Using @DEPRECATED placeholders
  if (title.includes("REQCOLLECTIONLINK")) {
    return `<div class="break-words font-normal text-sm">
        ${title.replace("REQCOLLECTIONLINK", collectionLink)}
      </div>`;
  }

  return `<div class="break-words">${title}</div>
          <div class="Giftbox-TextOverflowEllipsis Cropped-Text font-normal text-sm">${collectionLink}</div>`;
}

function getTitleTextSpendInCollection(offer, cartEmpty, remainingSpend) {
  let text = "";
  if (cartEmpty) {
    text = offer.title_empty_cart || "Spend REMAININGSPEND in the collection";
  } else {
    text =
      offer.title_condition_not_met ||
      "Spend REMAININGSPEND more in the collection";
  }
  const title = text.replace("REMAININGSPEND", String(remainingSpend));

  const collectionLink = `<a href="https://${getShopUrl()}/collections/${
    offer.target_shopify_collection_handle
  }">${offer.target_shopify_collection_name}</a>`;

  // Using @DEPRECATED placeholders
  if (title.includes("REQCOLLECTIONLINK")) {
    return `<div class="break-words font-normal text-sm">${title.replace(
      "REQCOLLECTIONLINK",
      collectionLink
    )}</div>`;
  }
  return `<div class="break-words">${title}</div>
          <div class="Giftbox-TextOverflowEllipsis Cropped-Text font-normal text-sm">${collectionLink}</div>`;
}

export default ({ offer, children, subtitle, isMultipleProduct }) => {
  const { passed, titleText, amount } = useThreshold(offer);
  // @ts-ignore
  // Potentialy percentage can be NaN the way its defined.
  // This would lead to it being rendered empty as can be seen below
  const percentage = ((offer.threshold - amount) / offer.threshold) * 100;

  return (
    <CardElement
      added={passed}
      isMultipleProduct={isMultipleProduct}
      offerId={offer.id}
    >
      <div>
        <div className="GiftBox-Card-Title text-base font-semibold leading-[1.375rem]">
          <HTMLSectionWithLink>{titleText}</HTMLSectionWithLink>
        </div>
        <div className="text-gray-600 GiftBox-CardLine">{subtitle}</div>
      </div>

      <div class="overflow-hidden rounded-full bg-gray-100 my-4">
        {percentage > 0 && percentage < 100 ? (
          <div
            class="h-1 rounded-full bg-primary-500"
            style={{ width: `${percentage}%` }}
          ></div>
        ) : (
          <div className="h-px"></div>
        )}
      </div>
      {children}
    </CardElement>
  );
};
